import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import { SALES_BOOKING_LINK } from "@/lib/constants";
import { allEmailProvidersDetails, cn } from "@/lib/utils";
import CancelInitiateDialog from "@/pages/billing/cancel-initiate-dialog";
import DowngradeInitiateDialog from "@/pages/billing/downgrade-initiate-dialog";
import SeedListDowngradeInitiateDialog from "@/pages/billing/seed-list-downgrade-initiate-dialog";
import SeedListCancelInitiateDialog from "@/pages/billing/seed-list-cancel-initiate-dialog";
import PlanCard from "@/pages/billing/plan-card";
import {
  customPlanData,
  PlanPrice,
  pricingData,
} from "@/pages/billing/pricing-data";
import { PlacementType } from "@/types/inboxPlacements.types";
import { PaymentInterval } from "@/types/subscription.types";
import { Check, ChevronDown, Minus, Plus } from "lucide-react";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SeedListContent } from "@/components/seed-list-content";
import { CustomPlanRequestDialog } from "@/components/custom-plan-request-dialog";

export const Route = createFileRoute("/_private/plans")({
  component: PlansPage,
});

const imagesMap = Object.values(allEmailProvidersDetails).reduce(
  (acc, provider) => {
    if (provider.type === "b2c") {
      acc[PlacementType.B2C].push(provider.icon);
    }
    if (provider.type === "b2b") {
      acc[PlacementType.B2B].push(provider.icon);
    }
    return acc;
  },
  {
    [PlacementType.B2C]: [],
    [PlacementType.B2B]: [],
    SEED_LIST: [],
  } as Record<string, string[]>
);

// Seed list provider icons
imagesMap.SEED_LIST = [
  "/providers-logos/gmail.png",
  "/providers-logos/outlook.png",
  "/providers-logos/yahoo.png",
  "/providers-logos/google-workspace.png",
  "/providers-logos/microsoft-365.png",
  "/providers-logos/aol.png",
  "/providers-logos/icloud.png",
];

const tabs = [
  {
    type: PlacementType.B2B,
    description: "Business to Business",
  },
  {
    type: PlacementType.B2C,
    description: "Business to Consumer",
  },
  {
    type: "SEED_LIST",
    description: "Seed List",
  },
];

const intervalOptions = [
  {
    value: PaymentInterval.MONTHLY,
    label: "Monthly",
    badge: "",
  },
  {
    value: PaymentInterval.QUARTERLY,
    label: "Quarterly",
    badge: "Save 10%",
  },
  {
    value: PaymentInterval.SEMI_YEARLY,
    label: "Semi-Yearly",
    badge: "Save 15%",
  },
  {
    value: PaymentInterval.ANNUAL,
    label: "Annually",
    badge: "Save 20%",
  },
];

function PlansPage() {
  const [count, setCount] = useState(1);
  const [interval, setInterval] = useState<keyof PlanPrice>(
    PaymentInterval.MONTHLY
  );
  const [selectedType, setSelectedType] = useState<PlacementType | "SEED_LIST">(
    PlacementType.B2B
  );
  const [isCustomPlanDialogOpen, setIsCustomPlanDialogOpen] = useState(false);

  const { data: activePlans } = useGetActiveSubscriptionPlans();

  const handleTabChange = (type: PlacementType | "SEED_LIST") => {
    setSelectedType(type);
    setCount(1);
  };

  const plans =
    selectedType === "SEED_LIST"
      ? []
      : pricingData[selectedType as PlacementType];

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between md:mb-6">
        <div className="flex items-center gap-3">
          <h1 className="page-heading">Plans</h1>
        </div>
      </div>

      <div className="py-2 md:py-0 md:mb-6 flex flex-col lg:flex-row justify-between items-center gap-4 sticky md:static top-[64px] z-20 bg-gray-50 md:bg-transparent">
        <Tabs
          defaultValue={interval}
          onValueChange={(value) => setInterval(value as keyof PlanPrice)}
          className="w-full hidden xs:block"
        >
          <TabsList className="flex lg:max-w-2xl w-full border">
            {intervalOptions.map((option) => (
              <TabsTrigger
                className="w-full md:w-auto md:flex-1"
                value={option.value}
              >
                {option.label}{" "}
                {option.badge && (
                  <Badge variant="default" className="ml-2 font-normal">
                    {/* <BadgePercent className="!size-3 mr-1" /> */}
                    {option.badge}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full px-3 justify-between xs:hidden capitalize"
            >
              {intervalOptions.find((tab) => tab.value === interval)?.label}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
            {intervalOptions.map((tab) => (
              <DropdownMenuItem
                key={tab.value}
                onSelect={() => setInterval(tab.value as keyof PlanPrice)}
              >
                {tab.label}
                {tab.badge && (
                  <Badge variant="default" className="ml-2 font-normal">
                    {tab.badge}
                  </Badge>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {selectedType !== "SEED_LIST" && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Buy Mailboxes
            </span>
            <div className="relative inline-flex h-9 items-center overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
              <Input
                type="number"
                min={1}
                max={5000}
                value={count}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === "0") {
                    setCount(1);
                  } else {
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 1) {
                      // Cap at 5000
                      setCount(Math.min(numValue, 5000));
                    }
                  }
                }}
                onBlur={(e) => {
                  const numValue = parseInt(e.target.value, 10);
                  if (e.target.value === "" || isNaN(numValue) || numValue < 1) {
                    setCount(1);
                  } else if (numValue > 5000) {
                    setCount(5000);
                  }
                }}
                className="min-w-[60px] w-20 h-full text-left border-none bg-white dark:bg-gray-900 px-3 py-0 text-sm font-medium text-gray-900 dark:text-gray-100 focus-visible:ring-0 focus:outline-none focus:ring-0 rounded-l-lg rounded-r-none"
              />
              <Button
                type="button"
                onClick={() => setCount(Math.max(1, count - 1))}
                disabled={count <= 1}
                className="h-full w-10 flex items-center justify-center border-l border-r border-gray-300 dark:border-gray-600 rounded-none bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none p-0 shadow-none"
              >
                <Minus size={14} strokeWidth={3} className="text-white" />
              </Button>
              <Button
                type="button"
                onClick={() => setCount(Math.min(count + 1, 5000))}
                disabled={count >= 5000}
                className="h-full w-10 flex items-center justify-center border-l border-gray-300 dark:border-gray-600 rounded-r-lg rounded-l-none bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none p-0 shadow-none"
              >
                <Plus size={14} strokeWidth={3} className="text-white" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tabs.map((item) => (
          <Button
            key={item.type}
            onClick={() => handleTabChange(item.type as PlacementType | "SEED_LIST")}
            variant={selectedType === item.type ? "outline" : "secondary"}
            data-state={selectedType === item.type ? "active" : ""}
            className="w-full px-4 py-3 h-full rounded-lg backdrop-blur-md data-[state=active]:bg-primary/[0.08] data-[state=active]:border-primary/50 bg-muted border border-white/10 flex flex-col"
          >
            <div className="text-foreground text-base font-medium">
              {item.description}
            </div>
            <div
              className={cn(
                "flex items-center gap-3 flex-wrap pt-3 pb-2",
                selectedType !== item.type && "grayscale opacity-80"
              )}
            >
              {imagesMap[item.type]?.map((image) => (
                <img
                  src={image}
                  alt={`${item.type} provider`}
                  width={20}
                  height={20}
                  key={image}
                />
              ))}
            </div>
          </Button>
        ))}
      </div>

      <div className="rounded-lg w-full mt-6">
        <div className="flex flex-col items-center gap-4 md:gap-8">
          {selectedType === "SEED_LIST" ? (
            <SeedListContent interval={interval} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {plans.map((plan, index) => (
                <PlanCard
                  key={`${plan.name}-${index}`}
                  plan={plan}
                  interval={interval}
                  count={count}
                  index={index}
                  lastPlanName={plans[index - 1]?.name || ""}
                  selectedType={selectedType}
                />
              ))}

              {/* Custom Plan */}
              <div
                className={cn(
                  "relative z-20 xl:col-span-full rounded-2xl border overflow-hidden backdrop-blur-sm p-6 h-full flex flex-col xl:flex-row xl:items-start xl:gap-8"
                )}
              >
                <img
                  src="/circle-grid.png"
                  alt="Consultation Banner"
                  width={500}
                  height={500}
                  className="absolute z-10 scale-125 top-1/2 -translate-y-1/2  right-0 translate-x-[30%] hidden xl:block"
                />
                <div className="flex xl:flex-1 flex-col gap-7 relative z-10">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {customPlanData.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {customPlanData.description}
                    </p>
                  </div>
                  <div className="w-full xl:max-w-[200px]">
                    <Button 
                      variant="default" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-normal text-sm px-4 py-2.5 shadow-md hover:shadow-lg transition-all duration-200"
                      onClick={() => setIsCustomPlanDialogOpen(true)}
                    >
                      Request Custom Plan
                    </Button>
                  </div>

                  <hr className="mb-4 border-dashed xl:hidden" />
                </div>

                <ul className="xl:flex-1 w-full grid grid-cols-1 xl:grid-cols-2 gap-2 text-sm relative z-10">
                  {customPlanData.features.map((feature, index) => (
                    <li
                      key={`custom-feature-${index}`}
                      className="flex items-start gap-3"
                    >
                      <Check className="w-5 h-5 mt-1 text-primary shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Plan management options */}
          {activePlans && activePlans.length > 0 && (
            <div className="flex justify-end w-full mt-4 gap-4">
              {selectedType === "SEED_LIST" ? (
                <>
                  {/* <SeedListDowngradeInitiateDialog /> */}
                  <SeedListCancelInitiateDialog />
                </>
              ) : (
                <>
                  <DowngradeInitiateDialog />
                  <CancelInitiateDialog />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Plan Request Dialog */}
      <CustomPlanRequestDialog
        open={isCustomPlanDialogOpen}
        onOpenChange={setIsCustomPlanDialogOpen}
        planType={
          selectedType === PlacementType.B2B
            ? "B2B"
            : selectedType === PlacementType.B2C
            ? "B2C"
            : undefined
        }
      />
    </div>
  );
}

const PromoCode = () => {
  const [showInput, setShowInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");

  const handleApply = () => {
    console.log(promoCode);
  };

  return (
    <div>
      {!showInput ? (
        <button
          onClick={() => setShowInput(!showInput)}
          className="underline text-muted-foreground text-sm"
        >
          Have a promo code?
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            placeholder="Enter Promo Code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
          />
          <Button variant="default">Apply</Button>
        </div>
      )}
    </div>
  );
};
